import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
} from "mobx";
import { inject, injectable } from "tsyringe";
import { ViewModel } from "../../lib/vvm";
import {
  type IWarehouseApi,
  type ProductJournalEntry,
  WAREHOUSE_API_TOKEN,
} from "../../api/WarehouseApi";
import { Action } from "../../types/entities";
import type { Product } from "../../stores/Product";
import {
  type INavigationStore,
  NAVIGATION_STORE_TOKEN,
  Screens,
} from "../../stores/navigation";
import {
  isBoxId,
  isProductId,
  openBoxScreenById,
} from "../shared/use-cases/entity-navigation";
import { deleteProductWithDestroyJournal } from "../shared/use-cases/delete-flow";
import {
  openProductsListByMeasure,
  openProductsListByTypeTag,
} from "../shared/use-cases/products-list-navigation";
import {
  mapProductHistoryItems,
  type ProductHistoryItem,
} from "./viewmodel/history";
import {
  applyProductUpdate,
  buildTakePutJournalPayload,
  buildTakePutUpdatePayload,
  resolveDestroySourceBoxId,
} from "./viewmodel/mutations";

export interface ProductPageViewProps {
  product: Product;
}

const PRODUCT_DELETED_MESSAGE =
  "Товар уже удален. Доступен только просмотр карточки и истории.";

@injectable()
export class ProductPageViewModel extends ViewModel<
  unknown,
  ProductPageViewProps
> {
  @observable.shallow
  history: ProductJournalEntry[] = [];

  @observable
  isHistoryLoading = false;

  @observable
  isTaking = false;

  @observable
  isPutting = false;

  @observable
  isDestroying = false;

  @observable
  isDestroyConfirmOpen = false;

  @observable
  actionError: string | null = null;

  @observable
  isProductDeleted = false;

  @observable
  lastKnownBoxId: string | null = null;

  constructor(
    @inject(WAREHOUSE_API_TOKEN) private readonly api: IWarehouseApi,
    @inject(NAVIGATION_STORE_TOKEN)
    private readonly navigationStore: INavigationStore,
  ) {
    super();

    makeObservable(this);
  }

  @computed
  get product(): Product {
    return this.viewProps.product;
  }

  @computed
  get canTake(): boolean {
    return (
      !this.isProductDeleted &&
      !this.isTaking &&
      !this.isPutting &&
      !this.isDestroying &&
      isProductId(this.product.id) &&
      !!this.product.parentId
    );
  }

  @computed
  get canPut(): boolean {
    return (
      !this.isProductDeleted &&
      !this.isTaking &&
      !this.isPutting &&
      !this.isDestroying &&
      isProductId(this.product.id) &&
      !this.product.parentId &&
      isBoxId(this.lastKnownBoxId)
    );
  }

  @computed
  get canDestroy(): boolean {
    return (
      !this.isProductDeleted &&
      !this.isTaking &&
      !this.isPutting &&
      !this.isDestroying &&
      isProductId(this.product.id)
    );
  }

  @computed
  get productLocation(): string {
    if (this.product.parentId) {
      return `в коробке ${this.product.parentId}`;
    }
    return "не в коробке";
  }

  @computed
  get historyItems(): ProductHistoryItem[] {
    return mapProductHistoryItems(this.history);
  }

  @computed
  get showDestroyAction(): boolean {
    return !this.isProductDeleted;
  }

  protected onViewMounted(): void {
    const currentBoxId = this.product.parentId;
    if (isBoxId(currentBoxId)) {
      this.lastKnownBoxId = currentBoxId;
    }

    void this.syncProductAvailability();
    void this.loadHistory();
  }

  @action.bound
  openEditProduct(): void {
    if (this.isProductDeleted) {
      this.actionError = PRODUCT_DELETED_MESSAGE;
      return;
    }

    this.navigationStore.setScreen(Screens.EditProduct, this.product);
  }

  @action.bound
  openTypeTagFilter(tag: string): void {
    openProductsListByTypeTag(this.navigationStore, tag);
  }

  @action.bound
  openMeasureFilter(): void {
    openProductsListByMeasure(this.navigationStore, this.product.measure);
  }

  @action.bound
  openLocationReference(): void {
    const parentId = this.product.parentId?.trim() || "";
    if (isBoxId(parentId)) {
      void this.openBoxById(parentId);
      return;
    }

    this.navigationStore.setScreen(Screens.ProductsList, {
      title: "Товары без коробки",
      placement: "withoutBox",
    });
  }

  @action.bound
  async loadHistory(): Promise<void> {
    if (!isProductId(this.product.id)) {
      runInAction(() => {
        this.history = [];
      });
      return;
    }

    runInAction(() => {
      this.isHistoryLoading = true;
    });

    try {
      const entries = await this.api.getJournalEntriesByProduct(
        this.product.id,
        30,
      );
      runInAction(() => {
        this.history = entries;
      });
    } catch (error) {
      runInAction(() => {
        this.actionError =
          error instanceof Error
            ? error.message
            : "Не удалось загрузить историю операций";
      });
    } finally {
      runInAction(() => {
        this.isHistoryLoading = false;
      });
    }
  }

  @action.bound
  async take(): Promise<void> {
    if (this.isTaking || this.isPutting) {
      return;
    }

    if (!isProductId(this.product.id)) {
      this.setActionError(
        "Нельзя выполнить действие: некорректный ID продукта",
      );
      return;
    }

    const fromBoxId = this.product.parentId;
    if (!isBoxId(fromBoxId)) {
      this.setActionError(
        "Нельзя взять продукт: он не привязан к корректной коробке",
      );
      return;
    }

    runInAction(() => {
      this.isTaking = true;
      this.actionError = null;
    });

    try {
      const updated = await this.api.updateProduct(
        this.product.id,
        buildTakePutUpdatePayload(this.product, null),
      );
      await this.api.createJournalEntry(
        buildTakePutJournalPayload(this.product.id, Action.take, fromBoxId),
      );
      runInAction(() => {
        applyProductUpdate(this.product, updated);
        this.lastKnownBoxId = fromBoxId;
      });
      await this.loadHistory();
    } catch (error) {
      runInAction(() => {
        this.actionError =
          error instanceof Error ? error.message : "Не удалось взять продукт";
      });
    } finally {
      runInAction(() => {
        this.isTaking = false;
      });
    }
  }

  @action.bound
  async put(): Promise<void> {
    if (this.isTaking || this.isPutting) {
      return;
    }

    if (!isProductId(this.product.id)) {
      this.setActionError(
        "Нельзя выполнить действие: некорректный ID продукта",
      );
      return;
    }

    if (this.product.parentId) {
      this.setActionError("Продукт уже находится в коробке");
      return;
    }

    const targetBoxId = this.lastKnownBoxId;
    if (!isBoxId(targetBoxId)) {
      this.setActionError(
        "Нельзя положить продукт: неизвестна целевая коробка",
      );
      return;
    }

    runInAction(() => {
      this.isPutting = true;
      this.actionError = null;
    });

    try {
      const updated = await this.api.updateProduct(
        this.product.id,
        buildTakePutUpdatePayload(this.product, targetBoxId),
      );
      await this.api.createJournalEntry(
        buildTakePutJournalPayload(this.product.id, Action.put, targetBoxId),
      );
      runInAction(() => {
        applyProductUpdate(this.product, updated);
      });
      await this.loadHistory();
    } catch (error) {
      runInAction(() => {
        this.actionError =
          error instanceof Error
            ? error.message
            : "Не удалось положить продукт";
      });
    } finally {
      runInAction(() => {
        this.isPutting = false;
      });
    }
  }

  @action.bound
  async destroyProduct(deleteReason?: string | null): Promise<void> {
    if (!this.canDestroy) {
      return;
    }

    runInAction(() => {
      this.isDestroying = true;
      this.actionError = null;
    });

    try {
      const sourceBoxId = resolveDestroySourceBoxId({
        currentParentId: this.product.parentId,
        lastKnownBoxId: this.lastKnownBoxId,
      });
      await deleteProductWithDestroyJournal({
        api: this.api,
        productId: this.product.id,
        sourceBoxId,
        deleteReason,
        sourceLabel: "с карточки товара",
      });
      this.navigationStore.setScreen(Screens.Main);
    } catch (error) {
      runInAction(() => {
        this.actionError =
          error instanceof Error
            ? error.message
            : "Не удалось уничтожить продукт";
      });
    } finally {
      runInAction(() => {
        this.isDestroying = false;
      });
    }
  }

  @action.bound
  openDestroyConfirm(): void {
    if (!this.canDestroy) {
      return;
    }

    this.actionError = null;
    this.isDestroyConfirmOpen = true;
  }

  @action.bound
  closeDestroyConfirm(): void {
    if (this.isDestroying) {
      return;
    }

    this.isDestroyConfirmOpen = false;
  }

  @action.bound
  confirmDestroyProduct(deleteReason?: string | null): void {
    if (this.isDestroying) {
      return;
    }

    void this.destroyProductFromConfirm(deleteReason);
  }

  private async destroyProductFromConfirm(
    deleteReason?: string | null,
  ): Promise<void> {
    await this.destroyProduct(deleteReason);
    runInAction(() => {
      this.isDestroyConfirmOpen = false;
    });
  }

  private setActionError(message: string | null): void {
    this.actionError = message;
  }

  private async syncProductAvailability(): Promise<void> {
    if (!isProductId(this.product.id)) {
      return;
    }

    try {
      const fresh = await this.api.getProductById(this.product.id);
      runInAction(() => {
        if (!fresh) {
          this.isProductDeleted = true;
          this.isDestroyConfirmOpen = false;
          this.actionError = PRODUCT_DELETED_MESSAGE;
          return;
        }

        this.isProductDeleted = false;
        applyProductUpdate(this.product, fresh);
        if (isBoxId(fresh.parentId)) {
          this.lastKnownBoxId = fresh.parentId;
        }
      });
    } catch {
      // Если сервер недоступен, оставляем текущее состояние без блокировки UI.
    }
  }

  private async openBoxById(boxId: string): Promise<void> {
    await openBoxScreenById({
      api: this.api,
      navigationStore: this.navigationStore,
      boxId,
      onNotFound: () => {
        runInAction(() => {
          this.actionError = "Коробка не найдена";
        });
      },
      onError: (error) => {
        runInAction(() => {
          this.actionError =
            error instanceof Error
              ? error.message
              : "Не удалось открыть карточку коробки";
        });
      },
    });
  }
}
